import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LogIn, Eye, EyeOff, Phone, Lock, KeyRound } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import DailyBonusPopup from '../components/DailyBonusPopup';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showDailyBonus, setShowDailyBonus] = useState(false);
    const [loginData, setLoginData] = useState(null);
    const [showChangePassword, setShowChangePassword] = useState(false);
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [changePwLoading, setChangePwLoading] = useState(false);
    const [showForgot, setShowForgot] = useState(false);
    const [forgotPhone, setForgotPhone] = useState('');
    const [forgotLoading, setForgotLoading] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);
    const [forgotMode, setForgotMode] = useState('email'); // 'email' | 'phone'
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotCode, setForgotCode] = useState('');
    const [forgotNewPass, setForgotNewPass] = useState('');
    const [forgotStep, setForgotStep] = useState(1); // email flow: 1=send code, 2=enter code
    const [forgotDone, setForgotDone] = useState(false);
    const { login, user } = useAuth();
    const navigate = useNavigate();

    // Show a friendly toast when the auth interceptor redirected here after a session expiry.
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get('session') === 'expired') {
            toast.info('Your session expired. Please sign in again.', { duration: 6000 });
            // Clean the query param so it does not re-fire on refresh
            window.history.replaceState({}, '', window.location.pathname);
        }
    }, []);

    // If the browser back-button (or a stale link) lands an already-authenticated
    // user back on /login, bounce them to their role home immediately. Without
    // this, tapping "Back" on iOS Safari from /lobby → /login left the user
    // staring at the sign-in form even though their session was still valid.
    useEffect(() => {
        if (!user || user === false) return;
        const dest = computeDestination(user.role);
        navigate(dest, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Shared destination logic — role default OR ?next= deep-link when
    // valid. Used by every post-login navigation (submit success, daily-
    // bonus modal close, change-password modal close) so a shared invite
    // link auto-return isn't clobbered by any interstitial modal.
    const computeDestination = (role) => {
        const roleDest = (
            role === 'admin' ? '/admin'
            : role === 'distributor' ? '/distributor'
            : role === 'operator' ? '/operator'
            : role === 'cashier' ? '/cashier'
            : role === 'ambassador' ? '/affiliate'
            : '/lobby'
        );
        try {
            const next = new URLSearchParams(window.location.search).get('next');
            const isPlayerLike = !role || role === 'user';
            if (next && isPlayerLike && next.startsWith('/')) return next;
        } catch { /* ignore */ }
        return roleDest;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);

        const result = await login(email, password);
        
        if (result.success) {
            // Stash one-time celebration flag for the Lobby to consume after navigation
            if (result.data?.birthday_bonus_awarded && result.data?.birthday_bonus_amount > 0) {
                try {
                    sessionStorage.setItem('birthdayBonus', JSON.stringify({
                        amount: result.data.birthday_bonus_amount,
                        ts: Date.now(),
                    }));
                } catch { /* ignore */ }
            }
            // Check if must change password first
            if (result.data?.must_change_password && result.data?.role !== 'admin') {
                setLoginData(result.data);
                setShowChangePassword(true);
                setLoading(false);
                return;
            }
            // Each role lands on its own portal
            // Each role lands on its own portal (via `computeDestination`).
            const destination = computeDestination(result.data?.role);
            const isPlayer = !result.data?.role || result.data.role === 'user';
            // Daily bonus: claimed (got credits) OR blocked (needs $20 load) → show popup
            // Only relevant for players; skip for admin/operator/cashier/ambassador
            if (isPlayer && (result.data?.daily_bonus_claimed || result.data?.daily_bonus_blocked)) {
                setLoginData(result.data);
                setShowDailyBonus(true);
            } else {
                navigate(destination, { replace: true });
            }
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    const handleChangePassword = async () => {
        if (newPassword.length < 6) {
            toast.error('Password must be at least 6 characters');
            return;
        }
        if (newPassword !== confirmPassword) {
            toast.error('Passwords do not match');
            return;
        }
        setChangePwLoading(true);
        try {
            await axios.post(`${API}/auth/change-password`, { new_password: newPassword }, { withCredentials: true });
            toast.success('Password changed successfully!');
            setShowChangePassword(false);
            setNewPassword('');
            setConfirmPassword('');
            // Continue to destination
            const role = loginData?.role;
            const destination = role === 'admin' ? '/admin'
                : role === 'distributor' ? '/distributor'
                : role === 'operator' ? '/operator'
                : role === 'cashier' ? '/cashier'
                : role === 'ambassador' ? '/affiliate'
                : '/lobby';
            if (loginData?.daily_bonus_claimed || loginData?.daily_bonus_blocked) {
                setShowDailyBonus(true);
            } else {
                navigate(destination);
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to change password');
        } finally {
            setChangePwLoading(false);
        }
    };

    const handleCloseDailyBonus = () => {
        setShowDailyBonus(false);
        navigate(computeDestination(loginData?.role), { replace: true });
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-4 py-12 bg-[#050505]">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md"
            >
                <div className="card-dark p-8">
                    <div className="text-center mb-8">
                        <Link to="/" className="inline-block">
                            <img
                                src="/betohalics-logo.png"
                                alt="Betohalics — Real Games. Real People. Real Wins."
                                className="mx-auto w-auto max-h-24 object-contain"
                                style={{ filter: 'drop-shadow(0 0 24px rgba(255,215,0,0.3))' }}
                                data-testid="login-logo-img"
                            />
                        </Link>
                        <p className="text-[#A0AAB2] mt-2">Welcome back!</p>
                    </div>

                    {error && (
                        <div className="mb-6 p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="login-error">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-white text-base">Phone or email</Label>
                            <div className="relative">
                                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AAB2]" />
                                <Input
                                    id="email"
                                    type="text"
                                    inputMode="text"
                                    autoCapitalize="off"
                                    autoCorrect="off"
                                    autoComplete="username"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Phone or email"
                                    className="pl-10 py-6 text-base bg-[#121212] border-white/10 text-white placeholder:text-[#A0AAB2]/50 focus:border-[#FFD700] focus:ring-[#FFD700]/20"
                                    required
                                    data-testid="login-email"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password" className="text-white text-base">Password</Label>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A0AAB2]" />
                                <Input
                                    id="password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="Enter your password"
                                    className="pl-10 pr-10 py-6 text-base bg-[#121212] border-white/10 text-white placeholder:text-[#A0AAB2]/50 focus:border-[#FFD700] focus:ring-[#FFD700]/20"
                                    required
                                    data-testid="login-password"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A0AAB2] hover:text-white transition-colors"
                                    data-testid="toggle-password"
                                >
                                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                                </button>
                            </div>
                        </div>

                        <Button
                            type="submit"
                            disabled={loading}
                            className="w-full btn-primary py-6 rounded-xl font-bold"
                            data-testid="login-submit"
                        >
                            {loading ? (
                                <span className="flex items-center justify-center">
                                    <span className="spinner mr-2" />
                                    Signing in...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center">
                                    <LogIn className="mr-2 w-5 h-5" />
                                    Sign In
                                </span>
                            )}
                        </Button>

                        <div className="text-center -mt-1">
                            <button
                                type="button"
                                onClick={() => {
                                    setForgotSent(false); setForgotPhone(''); setForgotMode('email');
                                    setForgotEmail(''); setForgotCode(''); setForgotNewPass('');
                                    setForgotStep(1); setForgotDone(false); setShowForgot(true);
                                }}
                                className="text-sm text-[#A0AAB2] hover:text-[#FFD700] underline-offset-4 hover:underline transition-colors"
                                data-testid="forgot-password-link"
                            >
                                Forgot password?
                            </button>
                        </div>
                    </form>

                    <div className="mt-6 text-center">
                        <p className="text-[#A0AAB2]">
                            New here?{' '}
                            <Link
                                to={`/register${window.location.search || ''}`}
                                className="text-[#FFD700] hover:text-[#F4C430] font-semibold"
                                data-testid="register-link"
                            >
                                Create an account
                            </Link>
                        </p>
                        <p className="text-[11px] text-[#A0AAB2] mt-2">
                            Want to earn $ per signup?{' '}
                            <Link to="/ambassadors" className="text-[#A855F7] hover:text-[#FF4DB8] font-semibold" data-testid="ambassador-link">
                                Become an ambassador →
                            </Link>
                        </p>
                    </div>
                </div>
            </motion.div>

            {/* Daily Bonus Popup */}
            <DailyBonusPopup 
                show={showDailyBonus} 
                onClose={handleCloseDailyBonus} 
                bonusData={loginData}
                onCashIn={() => {
                    // Take the player straight to the load-money flow: land on
                    // the lobby (or their role home) and auto-open the PayPal
                    // checkout via LoadMoneyHomeCard's onMount trigger. The
                    // `?load=1` query is a hint too — Lobby also honors it.
                    try { sessionStorage.setItem('autoOpenLoadMoney', '1'); } catch { /* ignore */ }
                    setShowDailyBonus(false);
                    const dest = computeDestination(loginData?.role);
                    const sep = dest.includes('?') ? '&' : '?';
                    navigate(`${dest}${sep}load=1`, { replace: true });
                }}
            />

            {/* Forgot Password Dialog */}
            <Dialog open={showForgot} onOpenChange={setShowForgot}>
                <DialogContent className="bg-[#121212] border-white/10">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-[#FFD700]" /> Forgot Password
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            Reset instantly with a code sent to your email, or request help by phone.
                        </DialogDescription>
                    </DialogHeader>

                    {/* Mode toggle */}
                    {!forgotSent && !forgotDone && (
                        <div className="flex gap-1 p-1 rounded-lg bg-[#0a0a0a] border border-white/10" data-testid="forgot-mode-toggle">
                            <button type="button" onClick={() => setForgotMode('email')}
                                className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-colors ${forgotMode === 'email' ? 'bg-[#FFD700] text-black' : 'text-[#A0AAB2] hover:text-white'}`}
                                data-testid="forgot-tab-email">By email (instant)</button>
                            <button type="button" onClick={() => setForgotMode('phone')}
                                className={`flex-1 py-1.5 rounded-md text-xs font-bold transition-colors ${forgotMode === 'phone' ? 'bg-[#FFD700] text-black' : 'text-[#A0AAB2] hover:text-white'}`}
                                data-testid="forgot-tab-phone">By phone (admin)</button>
                        </div>
                    )}

                    {/* ── EMAIL FLOW ── */}
                    {forgotMode === 'email' && !forgotDone && (
                        <>
                            {forgotStep === 1 ? (
                                <div className="space-y-3 py-2">
                                    <Label className="text-white">Email on your account</Label>
                                    <Input type="email" inputMode="email" value={forgotEmail}
                                        onChange={(e) => setForgotEmail(e.target.value)}
                                        placeholder="you@email.com"
                                        className="bg-[#0a0a0a] border-white/10 text-white"
                                        data-testid="forgot-email-input" />
                                    <p className="text-xs text-[#A0AAB2]">We'll email you a 6-digit code to set a new password.</p>
                                    <Button
                                        onClick={async () => {
                                            if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(forgotEmail.trim())) {
                                                toast.error('Enter a valid email'); return;
                                            }
                                            setForgotLoading(true);
                                            try {
                                                await axios.post(`${API}/auth/forgot-password/send-code`, { email: forgotEmail.trim().toLowerCase() });
                                                toast.success('If that email matches an account, a code is on its way.');
                                                setForgotStep(2);
                                            } catch {
                                                setForgotStep(2);
                                            } finally { setForgotLoading(false); }
                                        }}
                                        disabled={forgotLoading || !forgotEmail}
                                        className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold"
                                        data-testid="forgot-send-code">
                                        {forgotLoading ? 'Sending…' : 'Send reset code'}
                                    </Button>
                                </div>
                            ) : (
                                <div className="space-y-3 py-2">
                                    <Label className="text-white">6-digit code</Label>
                                    <Input inputMode="numeric" maxLength={6} value={forgotCode}
                                        onChange={(e) => setForgotCode(e.target.value.replace(/\D/g, ''))}
                                        placeholder="123456"
                                        className="bg-[#0a0a0a] border-white/10 text-white tracking-[0.4em] text-center text-lg"
                                        data-testid="forgot-code-input" />
                                    <Label className="text-white">New password</Label>
                                    <Input type="password" value={forgotNewPass}
                                        onChange={(e) => setForgotNewPass(e.target.value)}
                                        placeholder="At least 6 characters"
                                        className="bg-[#0a0a0a] border-white/10 text-white"
                                        data-testid="forgot-newpass-input" />
                                    <Button
                                        onClick={async () => {
                                            if (forgotCode.length !== 6) { toast.error('Enter the 6-digit code'); return; }
                                            if (forgotNewPass.length < 6) { toast.error('Password must be at least 6 characters'); return; }
                                            setForgotLoading(true);
                                            try {
                                                await axios.post(`${API}/auth/forgot-password/reset`, {
                                                    email: forgotEmail.trim().toLowerCase(), code: forgotCode, new_password: forgotNewPass,
                                                });
                                                setForgotDone(true);
                                            } catch (err) {
                                                toast.error(err.response?.data?.detail || 'Reset failed — check your code');
                                            } finally { setForgotLoading(false); }
                                        }}
                                        disabled={forgotLoading}
                                        className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold"
                                        data-testid="forgot-reset-submit">
                                        {forgotLoading ? 'Updating…' : 'Set new password'}
                                    </Button>
                                    <button type="button" onClick={() => setForgotStep(1)}
                                        className="w-full text-xs text-[#A0AAB2] hover:text-white" data-testid="forgot-resend">
                                        Didn't get it? Re-enter email
                                    </button>
                                </div>
                            )}
                        </>
                    )}

                    {forgotMode === 'email' && forgotDone && (
                        <div className="py-6 text-center space-y-3" data-testid="forgot-email-done">
                            <p className="text-[#39FF14] text-lg font-bold">Password updated</p>
                            <p className="text-[#A0AAB2] text-sm">You can now sign in with your new password.</p>
                            <Button onClick={() => setShowForgot(false)}
                                className="bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold" data-testid="forgot-done">
                                Got it
                            </Button>
                        </div>
                    )}

                    {/* ── PHONE FLOW (admin-assisted, unchanged) ── */}
                    {forgotMode === 'phone' && (forgotSent ? (
                        <div className="py-6 text-center space-y-3">
                            <p className="text-[#39FF14] text-lg font-bold">Request sent</p>
                            <p className="text-[#A0AAB2] text-sm">
                                If that phone matches an account, an admin will text or call you back with a new password.
                            </p>
                            <Button
                                onClick={() => setShowForgot(false)}
                                className="bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold"
                                data-testid="forgot-done"
                            >
                                Got it
                            </Button>
                        </div>
                    ) : (
                        <>
                            <div className="space-y-3 py-2">
                                <Label className="text-white">Phone Number</Label>
                                <Input
                                    type="tel"
                                    inputMode="tel"
                                    value={forgotPhone}
                                    onChange={(e) => setForgotPhone(e.target.value)}
                                    placeholder="e.g. (256) 555-1234"
                                    className="bg-[#0a0a0a] border-white/10 text-white"
                                    data-testid="forgot-phone-input"
                                />
                                <p className="text-xs text-[#A0AAB2]">
                                    Use the same phone you registered with. Admin reviews these in real time.
                                </p>
                            </div>
                            <DialogFooter>
                                <Button
                                    onClick={async () => {
                                        const digits = (forgotPhone || '').replace(/\D/g, '');
                                        if (digits.length < 7) {
                                            toast.error('Enter a valid phone number');
                                            return;
                                        }
                                        setForgotLoading(true);
                                        try {
                                            await axios.post(`${API}/auth/request-password-reset`, { phone: forgotPhone });
                                            setForgotSent(true);
                                        } catch {
                                            setForgotSent(true);
                                        } finally {
                                            setForgotLoading(false);
                                        }
                                    }}
                                    disabled={forgotLoading || !forgotPhone}
                                    className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold"
                                    data-testid="forgot-submit"
                                >
                                    {forgotLoading ? 'Sending…' : 'Request Password Reset'}
                                </Button>
                            </DialogFooter>
                        </>
                    ))}
                </DialogContent>
            </Dialog>


            {/* Forced Password Change Dialog */}
            <Dialog open={showChangePassword} onOpenChange={() => {}}>
                <DialogContent className="bg-[#121212] border-white/10 [&>button]:hidden">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <KeyRound className="w-5 h-5 text-[#FFD700]" /> Change Your Password
                        </DialogTitle>
                        <DialogDescription className="text-[#A0AAB2]">
                            For your security, you must set a new password before continuing.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div>
                            <Label className="text-white">New Password</Label>
                            <Input
                                type="password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Enter new password (min 6 chars)"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="force-change-pw-input"
                            />
                        </div>
                        <div>
                            <Label className="text-white">Confirm Password</Label>
                            <Input
                                type="password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                placeholder="Confirm new password"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white"
                                data-testid="force-confirm-pw-input"
                            />
                        </div>
                        {newPassword && confirmPassword && newPassword !== confirmPassword && (
                            <p className="text-xs text-[#FF3B30]">Passwords do not match</p>
                        )}
                    </div>
                    <DialogFooter>
                        <Button
                            onClick={handleChangePassword}
                            disabled={changePwLoading || newPassword.length < 6 || newPassword !== confirmPassword}
                            className="w-full bg-[#FFD700] text-black hover:bg-[#FFD700]/80 font-bold"
                            data-testid="force-change-pw-btn"
                        >
                            {changePwLoading ? 'Changing...' : 'Set New Password'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Login;
